// Updated AddRequest component with layout matching the visual design
import React, { useEffect, useState } from 'react';
import axios from 'utils/axios';
import { bytesToBase64 } from 'utils/functions';
import {
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Button,
  Box,
  Typography,
  Grid,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Divider
} from '@mui/material';
import { AiOutlineClose, AiOutlineReload } from 'react-icons/ai';
import { MdInsertDriveFile } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-datepicker';
import { bs } from 'date-fns/locale';
import { toast } from 'react-toastify';

const AddRequest = ({ onSuccess }) => {
  const { t } = useTranslation();
  const [requestData, setRequestData] = useState({
    name: '',
    description: '',
    personName: '',
    departmentId: null,
    office: '',
    officeId: '',
    externalId: '',
    requestStatusId: 1,
    documents: [],
    requestTypeId: null,
    institutionId: null,
    externalProtocolNumber: null,
    connectedRequestProtocolNumber: null,
    dateReceived: null,
    requestDate: null,
    isConfidential: false,
    procedureTypeId: null,
    connectedRequestId: null,
  });

  const [requestTypes, setRequestTypes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [adminProcedureTypes, setAdminProcedureTypes] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [requestsToConnect, setRequestsToConnect] = useState([]);
  const [selectOpen, setSelectOpen] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [fetchAttempted, setFetchAttempted] = useState(false);

  const selectedRequest = requestsToConnect.find(r => r.id === selectedRequestId);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setRequestData((prevData) => ({ ...prevData, [name]: value }));
  };

  function handleDateChange(fieldName, date) {
    setRequestData(prev => ({ ...prev, [fieldName]: date }));
  }

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [reqTypes, procTypes, deps, insts] = await Promise.all([
          axios.get('codebook?codebook=RequestTypes'),
          axios.get('codebook?codebook=AdministrativeProcedureTypes'),
          axios.get('codebook?codebook=Departments'),
          axios.get('codebook?codebook=Institutions')
        ]);
        setRequestTypes(reqTypes.data);
        setAdminProcedureTypes(procTypes.data);
        setDepartments(deps.data);
        setInstitutions(insts.data);
      } catch (err) {
        console.error('Error fetching codebooks', err);
      }
    };
    fetchAll();
  }, []);

  const fetchRequestsByWorkflowNumber = async (workflowNumber) => {
    try {
      const encoded = encodeURIComponent(workflowNumber);
      const response = await axios.get(`/requests/getByWorkflowNumber?workflowNumber=${encoded}`);

      setFetchAttempted(true);
      if (response.data?.length > 0) {
        setRequestsToConnect(response.data);
        setSelectOpen(true);
      }
      else {
        setRequestsToConnect([]);
      }
    } catch (err) { }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = bytesToBase64(reader.result);
        const newDocument = { base64, name: file.name };
        setRequestData((prevData) => ({
          ...prevData,
          documents: [...prevData.documents, newDocument],
        }));
      };
      reader.readAsArrayBuffer(file);
    });
  };

  const handleRemoveFile = (indexToRemove) => {
    setRequestData((prevData) => ({
      ...prevData,
      documents: prevData.documents.filter((_, index) => index !== indexToRemove),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const response = await axios.post('/requests/create', requestData);
      if (response.status === 200) {
        toast.success(t('request_submitted_success'));
        if (onSuccess) onSuccess(true);
      }
    } catch (error) {
      toast.error(t('request_submission_error'));
    } finally {
      setSubmitting(false);
    }
  };

  const department = departments.find(dep => dep.id === requestData.departmentId);
  const requestType = requestTypes.find(req => req.id === requestData.requestTypeId);
  const depCode = department?.code || 'XX';
  const reqCode = requestType?.code || 'XX';
  const numCode = 'XXXX';
  const yearCode = new Date().getFullYear();
  const formattedCode = `${depCode}-${reqCode}-${numCode}/${yearCode}`;

  return (
    <Box px={1} py={1}>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField label={t('title')} name="name" value={requestData.name} onChange={handleInputChange} fullWidth required />
          </Grid>
          <Grid item xs={12}>
            <TextField label={t('description')} name="description" value={requestData.description} onChange={handleInputChange} multiline rows={3} fullWidth />
          </Grid>

          <Grid container spacing={2} alignItems="center" sx={{ pl: 2, pt: 3 }}>
            <Grid item xs={12} sm={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={requestData.isConfidential}
                    onChange={(e) =>
                      setRequestData({ ...requestData, isConfidential: e.target.checked })
                    }
                  />
                }
                label={t('isConfidential')}
              />
            </Grid>

            <Grid item xs={12} sm={3} sx={{ pr: 0 }}>
              <FormControl fullWidth required size="small" sx={{ width: '180px' }}>
                <InputLabel>{t('administrativeProcedureType')}</InputLabel>
                <Select
                  name="procedureTypeId"
                  value={requestData.procedureTypeId}
                  onChange={handleInputChange}
                  label={t('administrativeProcedureType')}
                  sx={{
                    height: 44 
                  }}
                >
                  <MenuItem value={null}>
                    <em>{t('selectAdministrativeProcedureType')}</em>
                  </MenuItem>
                  {adminProcedureTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={3}>
              <DatePicker
                selected={
                  requestData.requestDate ? new Date(requestData.requestDate) : null
                }
                onChange={(date) => handleDateChange('requestDate', date)}
                locale={bs}
                dateFormat="dd.MM.yyyy"
                placeholderText={t('request_date')}
                //className="datePickerInput"
                wrapperClassName="fullWidthDatePicker"
                //className="muiDatePickerInput"

              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <DatePicker
                selected={
                  requestData.dateReceived ? new Date(requestData.dateReceived) : null
                }
                onChange={(date) => handleDateChange('dateReceived', date)}
                locale={bs}
                dateFormat="dd.MM.yyyy"
                placeholderText={t('date_of_receipt')}
                className="datePickerInput"
                wrapperClassName="fullWidthDatePicker"
              />
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Box border="1px dashed #ccc" borderRadius={2} p={2} textAlign="center">
              <Button variant="contained" component="label">
                {t('upload_documents')}
                <input type="file" hidden multiple onChange={handleFileChange} accept=".pdf,.docx,.txt,.jpg,.jpeg,.png" />
              </Button>
              {/* <Typography variant="caption" display="block" mt={1}>{t('or_drag_here')}</Typography> */}
            </Box>
            {requestData.documents.length > 0 && (
              <Box mt={1}>
                {requestData.documents.map((doc, idx) => (
                  <Box key={idx} display="flex" alignItems="center" gap={2} p={1} border="1px solid #ddd" borderRadius={1} mt={1}>
                    <MdInsertDriveFile fontSize="small" />
                    <Box flexGrow={1}>
                      <Typography variant="body2">{doc.name}</Typography>
                    </Box>
                    <IconButton onClick={() => handleRemoveFile(idx)}><AiOutlineClose /></IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>{t('department')}</InputLabel>
              <Select name="departmentId" value={requestData.departmentId} onChange={handleInputChange} label={t('department')}>
                <MenuItem value={null}><em>{t('select_department')}</em></MenuItem>
                {departments.map((dep) => <MenuItem key={dep.id} value={dep.id}>{dep.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>{t('request_type')}</InputLabel>
              <Select name="requestTypeId" value={requestData.requestTypeId} onChange={handleInputChange} label={t('request_type')}>
                <MenuItem value={null}><em>{t('select_request_type')}</em></MenuItem>
                {requestTypes.map((type) => <MenuItem key={type.id} value={type.id}>{type.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>{t('institution')}</InputLabel>
              <Select name="institutionId" value={requestData.institutionId} onChange={handleInputChange} label={t('institution')}>
                <MenuItem value={null}><em>{t('select_institution')}</em></MenuItem>
                {institutions.map((inst) => <MenuItem key={inst.id} value={inst.id}>{inst.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField label={t('external_protocol_number')} name="externalProtocolNumber" value={requestData.externalProtocolNumber} onChange={handleInputChange} fullWidth />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label={t('connectedRequestProtocolNumber')}
              name="connectedRequestProtocolNumber"
              value={requestData.connectedRequestProtocolNumber}
              onChange={handleInputChange}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => fetchRequestsByWorkflowNumber(requestData.connectedRequestProtocolNumber)} edge="end" disabled={!requestData.connectedRequestProtocolNumber?.trim()}>
                      <AiOutlineReload />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {fetchAttempted && requestsToConnect.length === 0 && !selectedRequestId && (
            <Grid item xs={12} display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" color="error">
                {t('no_request_found_with_protocol_number')}
              </Typography>
              <IconButton onClick={() => {
                setFetchAttempted(false);
                setRequestData((prevData) => ({
                  ...prevData,
                  connectedRequestProtocolNumber: '',
                }));
              }}>
                <AiOutlineClose />
              </IconButton>
            </Grid>
          )}

          {selectedRequestId && selectedRequest && (
            <Grid item xs={12} display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="body2">{t('connectedRequestProtocolNumber')}: {selectedRequest.workflowNumber || `Request ${selectedRequest.id}`}</Typography>
              <IconButton onClick={() => {
                setSelectedRequestId(null);
                setRequestData((prev) => ({ ...prev, connectedRequestId: null, connectedRequestProtocolNumber: '' }));
                setRequestsToConnect([]);
                setFetchAttempted(false);
              }}>
                <AiOutlineClose />
              </IconButton>
            </Grid>
          )}

          {requestsToConnect.length > 0 && !selectedRequestId && (
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>{t('select_request')}</InputLabel>
                <Select
                  value={selectedRequestId || ''}
                  label={t('select_request')}
                  open={selectOpen}
                  onOpen={() => setSelectOpen(true)}
                  onClose={() => setSelectOpen(false)}
                  onChange={(e) => {
                    setSelectedRequestId(e.target.value);
                    setRequestData((prev) => ({
                      ...prev,
                      connectedRequestId: e.target.value || null
                    }));
                    setSelectOpen(false);
                  }}
                >
                  {requestsToConnect.map((req) => (
                    <MenuItem key={req.id} value={req.id}>
                      {req.protocolNumber || req.workflowNumber || `Request ${req.id}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12}>
            <Typography variant="subtitle1">{t('protocol_number')}: <strong>{formattedCode}</strong></Typography>
          </Grid>

          <Grid item xs={12}>
            <Button variant="contained" color="primary" type="submit" fullWidth>{t('submit_request')}</Button>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default AddRequest;